/* One shared, native modal for header and bottom navigation. No API polling. */
if (!customElements.get('store-navigation')) {
  customElements.define('store-navigation', class extends HTMLElement {
    connectedCallback() {
      this.onClick = this.handleClick.bind(this);
      this.onClose = this.handleClose.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onSectionLoad = () => { this.bindDialog(); this.observeCart(); this.bindOfferMedia(); };
      this.onEditorSelect = this.handleEditorSelect.bind(this);
      this.onViewportResize = this.updateKeyboardVisibility.bind(this);
      this.onDrawerResize = () => { if (this.dialog?.open) this.positionDialog(this.opener); };
      this.onFocusOut = () => { document.documentElement.removeAttribute('data-store-keyboard-open'); this.updateOfferMedia(); };
      document.addEventListener('click', this.onClick);
      document.addEventListener('shopify:section:load', this.onSectionLoad);
      document.addEventListener('shopify:block:select', this.onEditorSelect);
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      window.visualViewport?.addEventListener('resize', this.onViewportResize);
      window.addEventListener('resize', this.onDrawerResize);
      document.addEventListener('focusin', this.onViewportResize);
      document.addEventListener('focusout', this.onFocusOut);
      // Also respect app dialogs that expose standard ARIA rather than a
      // native <dialog>. Observe state changes, never poll or rewrite app UI.
      this.onOverlayChange = () => {
        if (this.overlayFrame) return;
        this.overlayFrame = window.requestAnimationFrame(() => {
          this.overlayFrame = null;
          this.updateAppOverlayVisibility();
          this.updateOfferMedia();
        });
      };
      this.overlayObserver = new MutationObserver(this.onOverlayChange);
      this.overlayObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'class', 'style', 'aria-hidden', 'aria-modal'] });
      this.onOverlayAnimationEnd = event => {
        if (event.target.closest('[aria-modal="true"]')) this.onOverlayChange();
      };
      document.addEventListener('transitionend', this.onOverlayAnimationEnd, true);
      document.addEventListener('animationend', this.onOverlayAnimationEnd, true);
      this.updateAppOverlayVisibility();
      this.bindDialog();
      this.bindOfferMedia();
      this.observer = new MutationObserver(() => this.updateCartFromHeader());
      this.observeCart();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
          if (Number.isFinite(cartData?.item_count)) this.updateCount(cartData.item_count);
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      document.removeEventListener('shopify:block:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      window.visualViewport?.removeEventListener('resize', this.onViewportResize);
      window.removeEventListener('resize', this.onDrawerResize);
      document.removeEventListener('focusin', this.onViewportResize);
      document.removeEventListener('focusout', this.onFocusOut);
      this.onFocusOut();
      this.overlayObserver?.disconnect();
      document.removeEventListener('transitionend', this.onOverlayAnimationEnd, true);
      document.removeEventListener('animationend', this.onOverlayAnimationEnd, true);
      if (this.overlayFrame) window.cancelAnimationFrame(this.overlayFrame);
      document.documentElement.removeAttribute('data-store-app-overlay-open');
      this.dialog?.removeEventListener('close', this.onClose);
      this.dialog?.removeEventListener('keydown', this.onKeyDown);
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.mediaEvents?.abort();
      this.motionPreference?.removeEventListener('change', this.onMediaVisibility);
      this.mobilePreference?.removeEventListener('change', this.onMediaVisibility);
      this.querySelectorAll('.store-mobile-nav__offer-video').forEach(video => video.pause());
      document.body.classList.remove('store-category-open');
    }

    bindDialog() {
      const dialog = this.querySelector('#StoreCategoryDialog');
      if (dialog === this.dialog) return;
      this.dialog?.removeEventListener('close', this.onClose);
      this.dialog?.removeEventListener('keydown', this.onKeyDown);
      this.dialog = dialog;
      this.dialog?.addEventListener('close', this.onClose);
      this.dialog?.addEventListener('keydown', this.onKeyDown);
      if (!this.dialog?.open) this.handleClose();
    }

    bindOfferMedia() {
      if (typeof window.matchMedia !== 'function') return;
      this.mediaEvents?.abort();
      this.motionPreference?.removeEventListener('change', this.onMediaVisibility);
      this.mobilePreference?.removeEventListener('change', this.onMediaVisibility);
      this.mediaEvents = new AbortController();
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobilePreference = window.matchMedia('(max-width: 749px)');
      this.onMediaVisibility = () => this.updateOfferMedia();
      this.motionPreference.addEventListener('change', this.onMediaVisibility);
      this.mobilePreference.addEventListener('change', this.onMediaVisibility);
      document.addEventListener('visibilitychange', this.onMediaVisibility, { signal: this.mediaEvents.signal });
      window.visualViewport?.addEventListener('resize', this.onMediaVisibility, { signal: this.mediaEvents.signal });
      this.querySelectorAll('.store-mobile-nav__offer-video').forEach(video => this.bindOfferVideo(video));
      this.updateOfferMedia();
    }

    bindOfferVideo(video) {
      video.addEventListener('loadeddata', () => {
        video.setAttribute('data-ready', '');
        this.updateOfferMedia();
      }, { signal: this.mediaEvents.signal });
      video.addEventListener('error', () => { video.hidden = true; video.pause(); }, { signal: this.mediaEvents.signal });
    }

    updateOfferMedia() {
      if (!this.mobilePreference) return;
      const bar = this.querySelector('[data-store-mobile-bar]');
      const play = Boolean(bar && this.mobilePreference.matches && !this.motionPreference.matches && !document.hidden && getComputedStyle(bar).visibility !== 'hidden');
      // Desktop/reduced-motion views never instantiate or download the video.
      if (play) this.querySelectorAll('template[data-store-offer-video]').forEach(template => {
        const icon = template.parentElement;
        if (icon.querySelector('video')) return;
        const video = template.content.firstElementChild.cloneNode(true);
        video.muted = true;
        this.bindOfferVideo(video);
        icon.append(video);
      });
      this.querySelectorAll('.store-mobile-nav__offer-video').forEach(video => {
        if (play && !video.hidden) {
          if (video.readyState >= 2) video.setAttribute('data-ready', '');
          if (video.paused) video.play().catch(() => { video.hidden = true; });
        } else {
          if (!video.paused) video.pause();
          video.removeAttribute('data-ready');
        }
      });
    }

    updateKeyboardVisibility() {
      const viewport = window.visualViewport;
      const editing = document.activeElement?.matches('input:not([type="button"]):not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]');
      // Do not move the bar on scroll or browser-toolbar changes. Only suspend
      // it while an editable field and a significantly smaller viewport indicate a keyboard.
      document.documentElement.toggleAttribute('data-store-keyboard-open', Boolean(viewport && editing && window.innerHeight - viewport.height > 150));
      this.updateOfferMedia();
    }

    updateAppOverlayVisibility() {
      const open = [...document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]')].some(element => {
        if (element.closest('[hidden], [aria-hidden="true"]')) return false;
        // Closed details can still return cached layout rectangles in Chromium.
        for (let parent = element; parent; parent = parent.parentElement) {
          if (parent.tagName === 'DETAILS' && !parent.open) return false;
          const style = getComputedStyle(parent);
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
        }
        return element.getClientRects().length > 0;
      });
      document.documentElement.toggleAttribute('data-store-app-overlay-open', open);
    }

    positionDialog(opener) {
      if (!opener || !this.dialog) return;
      const rect = opener.getBoundingClientRect();
      if (!rect.width) return;
      // Physical button position owns the opening edge, independent of text direction.
      this.dialog.dataset.side = rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right';
    }

    openDialog(opener) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return false;
      if (opener) this.opener = opener;
      this.positionDialog(this.opener);
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('store-category-open');
      document.querySelectorAll('[data-store-category-open]').forEach(link => link.setAttribute('aria-expanded', 'true'));
      return true;
    }

    handleEditorSelect(event) {
      // Shopify can restore the selected section/block on entry. Selection
      // must never open the drawer; only its explicit navigation trigger does.
      if (!this.dialog?.open) return;
      const target = event.target;
      if (!this.dialog.contains(target) && !target.contains(this.dialog)) return;
      const branch = target.querySelector('details');
      if (branch) branch.open = true;
      for (let parent = target.parentElement; parent && parent !== this.dialog; parent = parent.parentElement) {
        if (parent.tagName === 'DETAILS') parent.open = true;
      }
      if (this.dialog.contains(target)) target.scrollIntoView({ block: 'nearest' });
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-store-category-open]');
      if (trigger && this.dialog && typeof this.dialog.showModal === 'function') {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.openDialog(trigger);
        return;
      }
      if (event.target.closest('[data-store-category-close]')) this.dialog?.close();
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
    }

    handleKeyDown(event) {
      if (event.key === 'Escape' && this.dialog.open) { this.dialog.close(); return; }
      if (event.key !== 'Tab' || !this.dialog.open) return;
      const elements = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')]
        .filter(element => {
          // Closed <details> descendants can still report layout rectangles in
          // Chromium. Exclude them explicitly before choosing the last Tab stop.
          for (let parent = element.parentElement; parent && parent !== this.dialog; parent = parent.parentElement) {
            if (parent.tagName === 'DETAILS' && !parent.open && element !== parent.querySelector(':scope > summary')) return false;
          }
          return element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
        });
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleClose() {
      document.body.classList.remove('store-category-open');
      document.querySelectorAll('[data-store-category-open]').forEach(link => link.setAttribute('aria-expanded', 'false'));
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    observeCart() {
      this.observer.disconnect();
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        this.observer.observe(bubble, { childList: true, subtree: true, characterData: true });
        this.updateCartFromHeader();
      }
    }

    updateCartFromHeader() {
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (!bubble) { this.updateCount(0); return; }
      const visible = bubble.querySelector('[aria-hidden="true"]');
      // At 100+ Dawn omits the visible number; keep its accessible count accurate.
      const countText = visible?.textContent || bubble.querySelector('.visually-hidden')?.textContent;
      const digits = countText?.match(/[0-9٠-٩]+/);
      if (digits) this.updateCount(Number(digits[0].replace(/[٠-٩]/g, digit => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))));
    }

    updateCount(count) {
      if (!Number.isSafeInteger(count) || count < 0) return;
      this.querySelectorAll('[data-store-cart-count]').forEach(badge => {
        badge.hidden = count === 0;
        badge.textContent = count > 99 ? '99+' : String(count);
      });
      this.querySelectorAll('[data-store-cart-count-label]').forEach(label => {
        const text = label.dataset.template.replace('[count]', String(count));
        if (label.textContent !== text) label.textContent = text;
      });
    }
  });
}
